import React, { memo, useState } from 'react'

import {
  Stack,
  StackProps,
  Flex,
  Image,
  Text,
  FlexProps,
  Box,
  Icon,
  PseudoBox,
  ImageZoom,
  BackgroundImage,
  IconSkeleton,
  Skeleton,
} from '@thirstycamel/ui'
import imageProxy from '../../utils/imageProxy'

///////////////////////////////////////////////////////////////////////////////////////////////////

const Bubble: React.FC<FlexProps & { shape: 'round' | 'shield' }> = ({
  shape,
  children,
  ...restProps
}) => {
  return (
    <Flex
      position="absolute"
      size="64px"
      align="center"
      justify="center"
      userSelect="none"
      {...restProps}
    >
      <Box position="absolute" top="50%" left="50%" size="100%" transform="translate(-50%,-50%)">
        {shape === 'round' ? (
          <Box borderRadius="50%" border="4px" borderColor="green.400" size="100%" />
        ) : shape === 'shield' ? (
          <Icon name="shield" color="pink.300" size="100%" />
        ) : null}
      </Box>

      {children}
    </Flex>
  )
}

///////////////////////////////////////////////////////////////////////////////////////////////////

const BubbleNew: React.FC = () => (
  <Bubble shape="round" top={0} left={0} transform="rotate(-10deg)">
    <Text color="gray.600" fontWeight="bold" fontSize="lg">
      NEW
    </Text>
  </Bubble>
)

///////////////////////////////////////////////////////////////////////////////////////////////////

const BubbleDeal: React.FC<{ price: number; quantity: number }> = ({ price, quantity }) => (
  <Bubble shape="shield" top={0} right={0} transform="rotate(10deg)">
    <Stack align="center" spacing={-1}>
      <Text color="gray.600" fontWeight="bold" fontSize="xs">
        {quantity} FOR
      </Text>
      <Text color="gray.600" fontWeight="black" fontSize="lg">
        ${price / 100}
      </Text>
    </Stack>
  </Bubble>
)

///////////////////////////////////////////////////////////////////////////////////////////////////

export interface ProductImagePreviewProps extends StackProps {
  images: any[]
  isNew?: boolean
  hideCarousel?: boolean
  deal?: { price: number; quantity: number }
  isFetching?: boolean
}

export const ProductImagePreview = ({
  images,
  isNew,
  deal,
  hideCarousel,
  isFetching,
  ...restProps
}: ProductImagePreviewProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const selectedSrc = images && imageProxy(images[selectedImageIndex]?.filename, { height: 400 })
  const selectedSrcZoom =
    images && imageProxy(images[selectedImageIndex]?.filename, { height: 1200 })

  return (
    <Box h="100%" {...restProps}>
      <Box flexGrow={1} position="relative" textAlign="center">
        {!images?.length ? (
          <IconSkeleton name="bottle-outline" h="300px" w="300px" mb={16} />
        ) : (
          <ImageZoom
            src={selectedSrc}
            srcZoom={selectedSrcZoom}
            w="100%"
            h={['30vh', , '350px']}
            maxHeight="350px"
          />
        )}

        {isNew && <BubbleNew />}
        {deal && <BubbleDeal {...deal} />}
      </Box>

      {!hideCarousel && (
        <Stack direction="row" flexShrink={0} px={10} py={6} display={['none', , 'flex']}>
          {!images?.length ? (
            <Flex
              justify="center"
              border="1px"
              borderColor="gray.200"
              size="100px"
              borderRadius="md"
              position="relative"
            >
              <IconSkeleton name="bottle-outline" h="80px" w="80px" />

              <Skeleton position="absolute" bottom="-1px" left="20%" right="20%" height="6px" />
            </Flex>
          ) : (
            images.map((image, index) => (
              <PseudoBox
                key={image.id}
                as="button"
                size="100px"
                onClick={() => setSelectedImageIndex(index)}
                border="1px"
                borderColor="gray.200"
                p={2}
                position="relative"
                borderRadius="md"
                transition="200ms ease box-shadow, 200ms ease border-color"
                _hover={{ boxShadow: 'md' }}
                _focus={{ borderColor: 'pink.500', outline: 0 }}
              >
                <BackgroundImage
                  src={imageProxy(image.filename, { height: 100 })}
                  w="100%"
                  h="100%"
                  size="contain"
                  backgroundPosition="center"
                />

                {index === selectedImageIndex && (
                  <Box
                    position="absolute"
                    bottom="-1px"
                    left="20%"
                    right="20%"
                    height="4px"
                    bg="pink.500"
                  />
                )}
              </PseudoBox>
            ))
          )}
        </Stack>
      )}
    </Box>
  )
}

export default ProductImagePreview
